import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Activity, ArrowRight, Award, CheckCircle2, ChevronLeft, ChevronRight, DollarSign, FileText, Loader2, Plus, Search, Shield, Sparkles, Truck, Upload, Users } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const LitixWizardPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [tortCategories, setTortCategories] = useState(null);
  const [loading, setLoading] = useState(false);
  const [caseId, setCaseId] = useState(null);
  const [report, setReport] = useState(null);
  
  const [caseSetup, setCaseSetup] = useState({
    case_name: '',
    case_number: '',
    court_type: 'state',
    court_name: '',
    state: 'California',
    county: '',
    judge_name: '',
    filing_date: '',
    parties: [
      { name: '', role: 'plaintiff', party_type: 'individual', address: '', phone: '', email: '', attorney_name: '', attorney_firm: '', attorney_bar_number: '' },
      { name: '', role: 'defendant', party_type: 'individual', address: '', phone: '', email: '', attorney_name: '', attorney_firm: '', attorney_bar_number: '' }
    ],
    torts: [],
    case_summary: '',
    damages_claimed: ''
  });
  
  const [documents, setDocuments] = useState([]);
  const [media, setMedia] = useState([]);
  
  // Load tort categories on mount
  useEffect(() => {
    axios.get(`${API}/wizard/torts`).then(res => setTortCategories(res.data.categories)).catch(() => {});
  }, []);
  
  const addParty = () => {
    setCaseSetup(prev => ({
      ...prev,
      parties: [...prev.parties, { name: '', role: 'defendant', party_type: 'individual', address: '', phone: '', email: '', attorney_name: '', attorney_firm: '', attorney_bar_number: '' }]
    }));
  };
  
  const updateParty = (index, field, value) => {
    setCaseSetup(prev => ({
      ...prev,
      parties: prev.parties.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };
  
  const toggleTort = (tort) => {
    setCaseSetup(prev => ({
      ...prev,
      torts: prev.torts.includes(tort) ? prev.torts.filter(t => t !== tort) : [...prev.torts, tort]
    }));
  };
  
  const createCase = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/wizard/case/create`, caseSetup);
      setCaseId(res.data.case_id);
      setStep(2);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const addDocument = async (name, type) => {
    if (!caseId) return;
    try {
      const res = await axios.post(`${API}/wizard/case/${caseId}/document?doc_name=${encodeURIComponent(name)}&doc_type=${encodeURIComponent(type)}`);
      setDocuments(prev => [...prev, res.data.document]);
    } catch (e) {
      console.error(e);
    }
  };
  
  const generateReport = async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/wizard/case/${caseId}/generate-report`);
      setReport(res.data.report);
      setStep(4);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const requestService = async (serviceType) => {
    if (!caseId) return;
    try {
      await axios.post(`${API}/wizard/case/${caseId}/service-request?service_type=${serviceType}`);
      alert(`${serviceType} request submitted!`);
    } catch (e) {
      console.error(e);
    }
  };
  
  const states = ["Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"];

  return (
    <main className="min-h-screen bg-slate-50" data-testid="litix-wizard-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="mb-8">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Fraunces', serif" }}>Litix Wizard</h1>
          <p className="text-muted-foreground">Complete case onboarding and management workflow</p>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {['Case Setup', 'Evidence', 'Analysis', 'Report', 'Services', 'Monitoring'].map((s, i) => (
            <div key={s} className="flex items-center gap-2 flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step > i + 1 ? 'bg-emerald-500 text-white' : step === i + 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {step > i + 1 ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-sm ${step === i + 1 ? 'font-bold text-blue-700' : 'text-slate-500'}`}>{s}</span>
              {i < 5 && <ChevronRight className="h-4 w-4 text-slate-300" />}
            </div>
          ))}
        </div>
        
        {/* Step 1: Case Setup */}
        {step === 1 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Case Information</CardTitle>
                <CardDescription>Enter the basic case details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Case Name *</Label>
                    <Input value={caseSetup.case_name} onChange={e => setCaseSetup({...caseSetup, case_name: e.target.value})} placeholder="Smith v. Jones" />
                  </div>
                  <div>
                    <Label>Case Number</Label>
                    <Input value={caseSetup.case_number} onChange={e => setCaseSetup({...caseSetup, case_number: e.target.value})} placeholder="2026-CV-12345" />
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Court Type</Label>
                    <Select value={caseSetup.court_type} onValueChange={v => setCaseSetup({...caseSetup, court_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="federal">Federal</SelectItem>
                        <SelectItem value="state">State</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>State</Label>
                    <Select value={caseSetup.state} onValueChange={v => setCaseSetup({...caseSetup, state: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>County</Label>
                    <Input value={caseSetup.county} onChange={e => setCaseSetup({...caseSetup, county: e.target.value})} placeholder="Los Angeles" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Judge Name</Label>
                    <Input value={caseSetup.judge_name} onChange={e => setCaseSetup({...caseSetup, judge_name: e.target.value})} placeholder="Hon. John Smith" />
                  </div>
                  <div>
                    <Label>Damages Claimed ($)</Label>
                    <Input type="number" value={caseSetup.damages_claimed} onChange={e => setCaseSetup({...caseSetup, damages_claimed: e.target.value})} placeholder="500000" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Parties */}
            <Card>
              <CardHeader>
                <CardTitle>Parties</CardTitle>
                <CardDescription>Add plaintiffs and defendants</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {caseSetup.parties.map((party, i) => (
                  <div key={i} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <Badge>{party.role}</Badge>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <div>
                        <Label>Name *</Label>
                        <Input value={party.name} onChange={e => updateParty(i, 'name', e.target.value)} placeholder="John Smith" />
                      </div>
                      <div>
                        <Label>Role</Label>
                        <Select value={party.role} onValueChange={v => updateParty(i, 'role', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="plaintiff">Plaintiff</SelectItem>
                            <SelectItem value="defendant">Defendant</SelectItem>
                            <SelectItem value="cross_complainant">Cross-Complainant</SelectItem>
                            <SelectItem value="cross_defendant">Cross-Defendant</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Type</Label>
                        <Select value={party.party_type} onValueChange={v => updateParty(i, 'party_type', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="individual">Individual</SelectItem>
                            <SelectItem value="corporation">Corporation</SelectItem>
                            <SelectItem value="llc">LLC</SelectItem>
                            <SelectItem value="partnership">Partnership</SelectItem>
                            <SelectItem value="government">Government</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <div>
                        <Label>Attorney Name</Label>
                        <Input value={party.attorney_name} onChange={e => updateParty(i, 'attorney_name', e.target.value)} placeholder="Jane Doe, Esq." />
                      </div>
                      <div>
                        <Label>Law Firm</Label>
                        <Input value={party.attorney_firm} onChange={e => updateParty(i, 'attorney_firm', e.target.value)} placeholder="Smith & Associates" />
                      </div>
                      <div>
                        <Label>Bar Number</Label>
                        <Input value={party.attorney_bar_number} onChange={e => updateParty(i, 'attorney_bar_number', e.target.value)} placeholder="123456" />
                      </div>
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={addParty}><Plus className="h-4 w-4 mr-2" /> Add Party</Button>
              </CardContent>
            </Card>
            
            {/* Torts Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Claimed Torts & Violations</CardTitle>
                <CardDescription>Select all applicable claims ({caseSetup.torts.length} selected)</CardDescription>
              </CardHeader>
              <CardContent>
                {tortCategories && (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {Object.entries(tortCategories).map(([key, cat]) => (
                      <div key={key} className="border rounded-lg p-3">
                        <h4 className="font-semibold mb-2 text-sm">{cat.label}</h4>
                        <div className="flex flex-wrap gap-2">
                          {cat.torts.map(tort => (
                            <button
                              key={tort}
                              onClick={() => toggleTort(tort)}
                              className={`px-3 py-1.5 text-xs rounded-full border transition-all ${caseSetup.torts.includes(tort) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}
                            >
                              {tort}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            <div className="flex justify-end">
              <Button onClick={createCase} disabled={loading || !caseSetup.case_name || caseSetup.torts.length === 0} className="px-8">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Create Case & Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Step 2: Evidence Upload */}
        {step === 2 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Document Upload</CardTitle>
                <CardDescription>Add case documents (contracts, correspondence, medical records, etc.)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  {['Contract', 'Correspondence', 'Medical Records', 'Police Report', 'Photographs', 'Invoices/Bills', 'Expert Report', 'Other'].map(type => (
                    <Button key={type} variant="outline" onClick={() => addDocument(`${type} Document`, type.toLowerCase())} className="justify-start">
                      <FileText className="h-4 w-4 mr-2" /> Add {type}
                    </Button>
                  ))}
                </div>
                {documents.length > 0 && (
                  <div className="border rounded-lg p-3">
                    <h4 className="font-semibold mb-2 text-sm">Uploaded ({documents.length})</h4>
                    <div className="space-y-2">
                      {documents.map(doc => (
                        <div key={doc.id} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          <span>{doc.name}</span>
                          <Badge variant="secondary">{doc.type}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}><ChevronLeft className="h-4 w-4 mr-2" /> Back</Button>
              <Button onClick={() => setStep(3)}>Continue to Analysis <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </div>
          </div>
        )}
        
        {/* Step 3: Analysis */}
        {step === 3 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Generate Litix Report</CardTitle>
                <CardDescription>AI will analyze all case data and generate a comprehensive report</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <h4 className="font-semibold mb-2">Report will include:</h4>
                  <ul className="space-y-1 text-sm">
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600" /> Case strength assessment</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600" /> Party analysis & investigation recommendations</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600" /> Settlement range estimation</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600" /> Document and evidence summary</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600" /> Recommended next actions</li>
                  </ul>
                </div>
                <Button onClick={generateReport} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Generate Litix Report
                </Button>
              </CardContent>
            </Card>
            
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}><ChevronLeft className="h-4 w-4 mr-2" /> Back</Button>
            </div>
          </div>
        )}
        
        {/* Step 4: Report */}
        {step === 4 && report && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  Litix Report Generated
                </CardTitle>
                <CardDescription>Generated {new Date(report.generated_at).toLocaleString()}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Case Assessment */}
                <div className="p-4 rounded-lg border">
                  <h4 className="font-semibold mb-3">Case Assessment</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-muted-foreground">Strength Score</span>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold text-blue-600">{report.sections.case_assessment.strength_score}</span>
                        <span className="text-sm">/100</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Liability Clarity</span>
                      <Badge className="capitalize">{report.sections.case_assessment.liability_clarity}</Badge>
                    </div>
                  </div>
                </div>
                
                {/* Settlement Analysis */}
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <h4 className="font-semibold mb-3">Settlement Analysis</h4>
                  <div className="text-2xl font-bold text-emerald-700">
                    ${report.sections.settlement_analysis.estimated_range_low.toLocaleString()} - ${report.sections.settlement_analysis.estimated_range_high.toLocaleString()}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Estimated settlement range based on case factors</p>
                </div>
                
                {/* Recommended Actions */}
                <div className="p-4 rounded-lg border">
                  <h4 className="font-semibold mb-3">Recommended Actions</h4>
                  <ul className="space-y-2">
                    {report.sections.recommended_actions.map((action, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <ArrowRight className="h-4 w-4 text-blue-600" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}><ChevronLeft className="h-4 w-4 mr-2" /> Back</Button>
              <Button onClick={() => setStep(5)}>Request Services <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </div>
          </div>
        )}
        
        {/* Step 5: Service Requests */}
        {step === 5 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Request Services</CardTitle>
                <CardDescription>Connect with our service networks to support your case</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { type: 'investigation', label: 'Investigation Report', desc: 'Background & deep investigation on parties', icon: Search },
                    { type: 'insurance', label: 'Insurance Analysis', desc: 'Coverage review & bad faith analysis', icon: Shield },
                    { type: 'paralegal', label: 'Paralegal Support', desc: 'Discovery & document prep assistance', icon: Users },
                    { type: 'financing', label: 'Legal Financing', desc: 'Pre-settlement funding options', icon: DollarSign },
                    { type: 'courier', label: 'Courier Service', desc: 'Same-day document delivery', icon: Truck },
                    { type: 'expert', label: 'Expert Witness', desc: 'Find qualified expert witnesses', icon: Award }
                  ].map(({ type, label, desc, icon: Icon }) => (
                    <Button key={type} variant="outline" onClick={() => requestService(type)} className="h-auto p-4 justify-start">
                      <div className="flex items-start gap-3">
                        <Icon className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div className="text-left">
                          <div className="font-semibold">{label}</div>
                          <div className="text-xs text-muted-foreground">{desc}</div>
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(4)}><ChevronLeft className="h-4 w-4 mr-2" /> Back</Button>
              <Button onClick={() => setStep(6)}>Setup Monitoring <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </div>
          </div>
        )}
        
        {/* Step 6: Monitoring */}
        {step === 6 && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  Case Setup Complete
                </CardTitle>
                <CardDescription>Your case is now being tracked in the Litix system</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                  <h4 className="font-semibold mb-2">What's Next?</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Case saved to your dashboard</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Reports accessible anytime</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Service requests tracked</li>
                    <li className="flex items-center gap-2"><Activity className="h-4 w-4 text-blue-600" /> Ongoing monitoring alerts enabled</li>
                  </ul>
                </div>
                
                <div className="flex gap-3">
                  <Button onClick={() => navigate('/platform/dashboard')} className="flex-1">
                    Go to Dashboard
                  </Button>
                  <Button variant="outline" onClick={() => { setStep(1); setCaseId(null); setReport(null); setDocuments([]); setCaseSetup({...caseSetup, case_name: '', torts: []}); }} className="flex-1">
                    Start New Case
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default LitixWizardPage;
